import React, { useMemo, useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';

interface ClassesFiltersProps {
  searchValue: string;
  genderValue: string;
  ageGroupValue: string;
  ageGroupOptions: string[];
  onSearchChange: (value: string) => void;
  onGenderChange: (value: string) => void;
  onAgeGroupChange: (value: string) => void;
  onClearFilters: () => void;
}

const genderOptions = ['Todas', 'Mista', 'Masculina', 'Feminina'];

const ClassesFilters: React.FC<ClassesFiltersProps> = ({
  searchValue,
  genderValue,
  ageGroupValue,
  ageGroupOptions,
  onSearchChange,
  onGenderChange,
  onAgeGroupChange,
  onClearFilters,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const activeFilterCount = useMemo(() => {
    return [searchValue, genderValue !== 'Todas' ? genderValue : '', ageGroupValue !== 'Todas' ? ageGroupValue : ''].filter(
      Boolean
    ).length;
  }, [ageGroupValue, genderValue, searchValue]);

  const activeFilterLabels = useMemo(() => {
    return [
      genderValue !== 'Todas' ? `Sexo: ${genderValue}` : '',
      ageGroupValue !== 'Todas' ? `Faixa: ${ageGroupValue}` : '',
    ].filter(Boolean);
  }, [ageGroupValue, genderValue]);

  return (
    <section className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            placeholder="Buscar turma, professores ou faixa..."
            className="pl-11"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        </div>

        <Button
          variant="outline"
          size="icon"
          className="relative h-12 w-12 border-gray-200 text-gray-500"
          onClick={() => setIsPanelOpen((current) => !current)}
        >
          <SlidersHorizontal size={18} />
          {activeFilterCount > 0 && (
            <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </div>

      {!isPanelOpen && activeFilterCount > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white p-3 shadow-[0_18px_40px_-24px_rgba(15,23,42,0.3)]">
          <div className="flex flex-wrap gap-2">
            {activeFilterLabels.map((label) => (
              <span
                key={label}
                className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700"
              >
                {label}
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={onClearFilters}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600"
          >
            <X size={12} />
            Limpar
          </button>
        </div>
      )}

      {isPanelOpen && (
        <div className="space-y-3 rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-24px_rgba(15,23,42,0.3)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Filtros</p>
              <p className="text-xs text-gray-500">Todos os filtros desta tela sao refletidos na URL.</p>
            </div>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={onClearFilters}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600"
              >
                <X size={12} />
                Limpar
              </button>
            )}
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Sexo da turma</p>
            <div className="flex flex-wrap gap-2">
              {genderOptions.map((option) => {
                const isActive = genderValue === option;

                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => onGenderChange(option)}
                    className={`rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                      isActive ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Faixa etaria</p>
            <div className="flex flex-wrap gap-2">
              {ageGroupOptions.map((option) => {
                const isActive = ageGroupValue === option;

                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => onAgeGroupChange(option)}
                    className={`rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                      isActive ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ClassesFilters;
